
// src/store/slices/toDoSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import toDoApi from 'api/toDoApi';

export const fetchTasks = createAsyncThunk(
  'toDo/fetchTasks',
  async ({ 
    page = 1, 
    pageSize = 10, 
    search = '', 
    type,
    start_date = '',
    end_date = '',
    date_range = '',
    status = '',
  }, { rejectWithValue }) => {
    try {
      const response = await toDoApi.getAll(page, pageSize, search, type, start_date, end_date, date_range, status);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load tasks');
    }
  }
);

export const fetchTaskCategories = createAsyncThunk(
  'toDo/fetchTaskCategories',
  async ({ page = 1, pageSize = 10, search = '', type = '', categoryType = '' }, { rejectWithValue }) => {
    try {
      const response = await toDoApi.getCategories(page, pageSize, search, type, categoryType);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load task categories');
    }
  }
);

export const createTask = createAsyncThunk(
  'toDo/createTask',
  async (formData, { rejectWithValue }) => {          // ← renamed param to formData (clarity)
    try {
      const response = await toDoApi.create(formData);  // ← pass FormData directly
      return response.data;
    } catch (err) {
        const msg = err.response.data.message || 'Failed to create task';
      
      return rejectWithValue(msg);
    }
  } 
);

export const updateTask = createAsyncThunk(
  'toDo/updateTask',
  async ({ id, data: formData }, { rejectWithValue }) => {   // ← renamed to formData
    try {
      const response = await toDoApi.update(id, formData);    // ← pass FormData
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.response?.data?.errors?.attachments?.[0] ||
        'Failed to update task'
      );
    }
  }
);

export const deleteTask = createAsyncThunk(
  'toDo/deleteTask',
  async (id, { rejectWithValue }) => {
    try {
      await toDoApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete task');
    }
  }
);

export const exportTasks = createAsyncThunk(
  'toDo/exportTasks',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await toDoApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

// ── Projects CRUD ────────────────────────────────────────────────
export const fetchProjects = createAsyncThunk(
  'toDo/fetchProjects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await toDoApi.getProjects();
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load projects');
    }
  }
);

export const createProject = createAsyncThunk(
  'toDo/createProject',
  async (data, { rejectWithValue }) => {
    try {
      const response = await toDoApi.createProject(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create project');
    }
  }
);

export const updateProject = createAsyncThunk(
  'toDo/updateProject',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await toDoApi.updateProject(id, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update project');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'toDo/deleteProject',
  async (id, { rejectWithValue }) => {
    try {
      await toDoApi.deleteProject(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to delete project');
    }
  }
);

// ── Labels CRUD ──────────────────────────────────────────────────
export const fetchLabels = createAsyncThunk(
  'toDo/fetchLabels',
  async ({type = "", employee_id = "" }, { rejectWithValue }) => {
    try {
      const response = await toDoApi.getLabels(type, employee_id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to load labels');
    }
  }
);

export const fetchLabelsAll = createAsyncThunk(
  'toDo/fetchLabelsAll',
  async ({type = "", employee_id = "" }, { rejectWithValue }) => {
    try {
      const response = await toDoApi.getLabelsAll(type, employee_id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to load labels');
    }
  }
);

export const createLabel = createAsyncThunk(
  'toDo/createLabel',
  async (data, { rejectWithValue }) => {
    try {
      const response = await toDoApi.createLabel(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create label');
    }
  }
);

export const updateLabel = createAsyncThunk(
  'toDo/updateLabel',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await toDoApi.updateLabel(id, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update label');
    }
  }
);

export const deleteLabel = createAsyncThunk(
  'toDo/deleteLabel',
  async (id, { rejectWithValue }) => {
    try {
      await toDoApi.deleteLabel(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to delete label');
    }
  }
);

const toDoSlice = createSlice({
  name: 'toDo',
  initialState: {
    tasks: [],
    projects: [],
    labels: [],
    labelsAll: [],
    pagination: { count: 0, page_size: 10 },
    categories: [],
    categoriesPagination: { count: 0, page_size: 10, current_page: 1, total_pages: 1 },
    isLoadingCategories: false,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Tasks
      .addCase(fetchTasks.pending, (state) => { state.isLoading = true; })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasks = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchTaskCategories.pending, (state) => { state.isLoadingCategories = true; })
      .addCase(fetchTaskCategories.fulfilled, (state, action) => {
        state.isLoadingCategories = false;
        state.categories = action.payload.data || [];
        state.categoriesPagination = action.payload.pagination || state.categoriesPagination;
      })
      .addCase(fetchTaskCategories.rejected, (state, action) => {
        state.isLoadingCategories = false;
        state.error = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks.unshift(action.payload);
        state.pagination.count += 1;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        const idx = state.tasks.findIndex(t => t.id === action.payload.id);
        if (idx !== -1) state.tasks[idx] = action.payload;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter(t => t.id !== action.payload);
        state.pagination.count -= 1;
      })

      // Projects
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projects = action.payload || [];
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects.push(action.payload);
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        const idx = state.projects.findIndex(p => p.id === action.payload.id);
        if (idx !== -1) state.projects[idx] = action.payload;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter(p => p.id !== action.payload);
      })

      // Labels
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.labels = action.payload || [];
      })
      .addCase(createLabel.fulfilled, (state, action) => {
        state.labels.push(action.payload);
      })
      .addCase(updateLabel.fulfilled, (state, action) => {
        const idx = state.labels.findIndex(l => l.id === action.payload.id);
        if (idx !== -1) state.labels[idx] = action.payload;
      })
      .addCase(deleteLabel.fulfilled, (state, action) => {
        state.labels = state.labels.filter(l => l.id !== action.payload);
      })

      // Label All
        .addCase(fetchLabelsAll.fulfilled, (state, action) => {
        state.labelsAll = action.payload || [];
      });
  },
});

export const { clearError } = toDoSlice.actions;
export default toDoSlice.reducer;